import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import type { EquityPoint, StrategyTrade } from '@/services/strategyService';

// ── Equity Curve ────────────────────────────────────────────

interface EquityCurveChartProps {
  equityCurve: EquityPoint[];
  height?: number;
}

export const EquityCurveChart: React.FC<EquityCurveChartProps> = ({ equityCurve, height = 200 }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<any>(null);
  const [tip, setTip] = useState<{ x: number; date: string; equity: number } | null>(null);

  // lightweight-charts needs strictly-ascending, unique-time points. Backtest equity points can
  // share a timestamp (multiple exits on one bar) — dedupe by second, keep the last value.
  const points = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of equityCurve) {
      const t = Math.floor(new Date(p.time).getTime() / 1000);
      if (Number.isFinite(t)) m.set(t, p.equity);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([time, value]) => ({ time, value }));
  }, [equityCurve]);

  const isPositive = points.length ? points[points.length - 1].value >= points[0].value : true;

  useEffect(() => {
    if (!containerRef.current || points.length === 0) return;
    let cancelled = false;
    (async () => {
      const lc = await import('lightweight-charts');
      const { createChart, AreaSeries } = lc as any;
      if (cancelled || !containerRef.current) return;
      if (chartRef.current) { try { chartRef.current.remove(); } catch { /* gone */ } chartRef.current = null; }

      const isDark = document.documentElement.classList.contains('dark');
      const chart = createChart(containerRef.current, {
        autoSize: true,
        layout: { background: { color: 'transparent' }, textColor: isDark ? '#d1d4dc' : '#333' },
        grid: {
          vertLines: { color: isDark ? 'rgba(197,203,206,0.08)' : 'rgba(197,203,206,0.2)' },
          horzLines: { color: isDark ? 'rgba(197,203,206,0.08)' : 'rgba(197,203,206,0.2)' },
        },
        timeScale: {
          timeVisible: true, secondsVisible: false,
          borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc',
        },
        rightPriceScale: { borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc' },
        crosshair: { mode: 1 }, // magnet
      });
      const color = isPositive ? '#22c55e' : '#ef4444';
      const series = chart.addSeries(AreaSeries, {
        lineColor: color,
        topColor: isPositive ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)',
        bottomColor: 'rgba(0,0,0,0)',
        lineWidth: 2,
        priceFormat: { type: 'price', precision: 0, minMove: 1 },
      });
      series.setData(points);
      chart.timeScale().fitContent();

      // Hover → date + equity tooltip.
      chart.subscribeCrosshairMove((param: any) => {
        const d = param.time && param.point ? param.seriesData?.get(series) : null;
        if (!d) { setTip(null); return; }
        setTip({
          x: param.point.x,
          date: new Date((param.time as number) * 1000).toLocaleString(),
          equity: d.value ?? d.close,
        });
      });
      chartRef.current = chart;
    })();
    return () => {
      cancelled = true;
      if (chartRef.current) { try { chartRef.current.remove(); } catch { /* gone */ } chartRef.current = null; }
    };
  }, [points, isPositive]);

  if (equityCurve.length === 0) return null;

  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm">Equity Curve <span className="text-[10px] text-muted-foreground font-normal">— scroll to zoom, drag to pan, hover for value</span></CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div className="relative" style={{ height }}>
          <div ref={containerRef} className="w-full h-full" />
          {tip && (
            <div className="pointer-events-none absolute z-10 rounded border bg-popover/95 px-2 py-1 text-[11px] shadow-md top-1"
              style={{ left: Math.max(4, Math.min(tip.x + 12, (containerRef.current?.clientWidth ?? 300) - 140)) }}>
              <div className="text-muted-foreground whitespace-nowrap">{tip.date}</div>
              <div className="tabular-nums font-medium">${tip.equity.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ── P&L Distribution Histogram ──────────────────────────────

interface PnLDistributionChartProps {
  trades: StrategyTrade[];
  height?: number;
}

export const PnLDistributionChart: React.FC<PnLDistributionChartProps> = ({ trades, height = 180 }) => {
  const { bins, maxCount } = useMemo(() => {
    const pnls = trades.map(t => t.realizedPnL).filter(p => p !== 0);
    if (pnls.length === 0) return { bins: [], maxCount: 0 };

    const min = Math.min(...pnls);
    const max = Math.max(...pnls);
    const range = max - min || 1;
    const binCount = Math.min(20, Math.max(5, Math.ceil(pnls.length / 3)));
    const binWidth = range / binCount;

    const buckets: { low: number; high: number; count: number; isPositive: boolean }[] = [];
    for (let i = 0; i < binCount; i++) {
      const low = min + i * binWidth;
      const high = low + binWidth;
      buckets.push({
        low,
        high,
        count: pnls.filter(p => p >= low && (i === binCount - 1 ? p <= high : p < high)).length,
        isPositive: (low + high) / 2 >= 0,
      });
    }

    return { bins: buckets, maxCount: Math.max(...buckets.map(b => b.count), 1) };
  }, [trades]);

  if (bins.length === 0) return null;

  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm">P&L Distribution</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div style={{ height }} className="flex items-end gap-[1px]">
          {bins.map((bin, i) => {
            const pct = (bin.count / maxCount) * 100;
            return (
              <div
                key={i}
                className="flex-1 relative group"
                style={{ height: '100%' }}
              >
                <div
                  className={`absolute bottom-0 left-0 right-0 rounded-t-sm transition-all ${bin.isPositive ? 'bg-green-500/70' : 'bg-red-500/70'}`}
                  style={{ height: `${Math.max(pct, 2)}%` }}
                />
                {/* Tooltip */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover border rounded px-1.5 py-0.5 text-[9px] whitespace-nowrap z-10 shadow-md">
                  ${bin.low.toFixed(0)} – ${bin.high.toFixed(0)}: {bin.count}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
          <span>${bins[0].low.toFixed(0)}</span>
          <span>P&L ($)</span>
          <span>${bins[bins.length - 1].high.toFixed(0)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// ── MFE / MAE Scatter Plot ──────────────────────────────────

interface MfeScatterPlotProps {
  trades: StrategyTrade[];
  height?: number;
}

export const MfeScatterPlot: React.FC<MfeScatterPlotProps> = ({ trades, height = 180 }) => {
  const { points, maxVal } = useMemo(() => {
    const pts = trades
      .filter(t => t.maxFavorableExcursion > 0 || t.maxAdverseExcursion > 0)
      .map(t => ({
        mfe: t.maxFavorableExcursion,
        mae: t.maxAdverseExcursion,
        pnl: t.realizedPnL,
        symbol: t.symbol,
      }));
    if (pts.length === 0) return { points: pts, maxVal: 1 };

    const maxMfe = Math.max(...pts.map(p => p.mfe));
    const maxMae = Math.max(...pts.map(p => p.mae));
    return { points: pts, maxVal: Math.max(maxMfe, maxMae, 0.01) };
  }, [trades]);

  if (points.length === 0) return null;

  const w = 100;
  const h = 100;

  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-sm">MFE vs MAE</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div style={{ height }} className="relative">
          <svg viewBox="-8 -2 112 108" className="w-full h-full">
            {/* Grid */}
            <line x1="0" x2={w} y1={h} y2={h} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.3" />
            <line x1="0" x2="0" y1="0" y2={h} stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.3" />
            {/* Diagonal guide (MFE = MAE) */}
            <line x1="0" x2={w} y1={h} y2="0" stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.3" strokeDasharray="2,2" />
            {/* Points */}
            {points.map((p, i) => {
              const cx = (p.mae / maxVal) * w;
              const cy = h - (p.mfe / maxVal) * h;
              const color = p.pnl >= 0 ? '#22c55e' : '#ef4444';
              return (
                <g key={i}>
                  <circle cx={cx} cy={cy} r="1.5" fill={color} opacity={0.7} />
                  {/* Hover area */}
                  <circle cx={cx} cy={cy} r="3" fill="transparent" className="cursor-pointer">
                    <title>{p.symbol}: MFE ${p.mfe.toFixed(2)}, MAE ${p.mae.toFixed(2)}, P&L ${p.pnl.toFixed(2)}</title>
                  </circle>
                </g>
              );
            })}
          </svg>
          {/* Axis labels */}
          <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] text-muted-foreground">
            MAE ($) →
          </div>
          <div className="absolute top-1/2 -left-1 -translate-y-1/2 -rotate-90 text-[9px] text-muted-foreground whitespace-nowrap origin-center">
            ← MFE ($)
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
